import { Grid, Alert, Button, Typography, Divider, Box, IconButton, Chip } from "@mui/material";
import { useState, useEffect } from "react";
import { useForm, useFieldArray, useFormContext } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import { FormProvider, RHFTextField, RHFSelectField, RHFDateField } from "../../common/hook-form";
import { LoadingButton } from "@mui/lab";
import {
    Add as AddIcon,
    RemoveCircleOutline as RemoveIcon,
} from '@mui/icons-material';
import { useSnackbar } from "notistack";
import { apiConstants, postGeneric } from "../../common/constants";
import { format } from "date-fns";

const getDayOfWeek = (date) => {
    if (!date) return "";
    const days = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
    return days[new Date(date).getDay()];
};

function HolidayEntry({ holidayIndex, onRemove, canRemove }) {
    const { control, watch, setValue } = useFormContext();
    const { fields: dateFields, append: appendDate, remove: removeDate } = useFieldArray({
        control,
        name: `holidays.${holidayIndex}.dates`,
    });

    const hType = watch(`holidays.${holidayIndex}.hTYpe`);
    const isRegional = hType === "REGIONAL";
    const watchedDates = watch(`holidays.${holidayIndex}.dates`);

    useEffect(() => {
        if (watchedDates && Array.isArray(watchedDates)) {
            watchedDates.forEach((dateItem, dateIndex) => {
                if (dateItem?.date) {
                    const dayOfWeek = getDayOfWeek(dateItem.date);
                    if (dateItem.day !== dayOfWeek) {
                        setValue(`holidays.${holidayIndex}.dates.${dateIndex}.day`, dayOfWeek);
                    }
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedDates]);

    return (
        <Grid item xs={12}>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, backgroundColor: '#fafafa' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Chip label={`Holiday #${holidayIndex + 1}`} color="primary" size="small" variant="outlined" />
                    {canRemove && (
                        <Button
                            onClick={onRemove}
                            color="error"
                            size="small"
                            startIcon={<RemoveIcon />}
                        >
                            Remove Entry
                        </Button>
                    )}
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <RHFSelectField
                            name={`holidays.${holidayIndex}.hTYpe`}
                            label="Holiday Type *"
                            options={[
                                { value: "NATIONAL", label: "National" },
                                { value: "REGIONAL", label: "Regional" },
                            ]}
                        />
                    </Grid>
                    {isRegional && (
                        <Grid item xs={12} md={4}>
                            <RHFTextField
                                name={`holidays.${holidayIndex}.fromToPincodes.to`}
                                label="To Pincode *"
                            />
                        </Grid>
                    )}
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle2" color="text.secondary">Dates</Typography>
                    <Button
                        onClick={() => appendDate({ date: null, event: "", day: "" })}
                        size="small"
                        startIcon={<AddIcon />}
                    >
                        Add Date
                    </Button>
                </Box>

                {dateFields.map((dateField, dateIndex) => (
                    <Grid container spacing={2} key={dateField.id} sx={{ mb: 1.5 }}>
                        <Grid item xs={12} md={3}>
                            <RHFDateField
                                name={`holidays.${holidayIndex}.dates.${dateIndex}.date`}
                                label="Date *"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <RHFTextField
                                name={`holidays.${holidayIndex}.dates.${dateIndex}.event`}
                                label="Event Name *"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <RHFSelectField
                                name={`holidays.${holidayIndex}.dates.${dateIndex}.day`}
                                label="Day"
                                options={[
                                    { value: "SUNDAY", label: "Sunday" },
                                    { value: "MONDAY", label: "Monday" },
                                    { value: "TUESDAY", label: "Tuesday" },
                                    { value: "WEDNESDAY", label: "Wednesday" },
                                    { value: "THURSDAY", label: "Thursday" },
                                    { value: "FRIDAY", label: "Friday" },
                                    { value: "SATURDAY", label: "Saturday" },
                                ]}
                            />
                        </Grid>
                        <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                            {dateFields.length > 1 && (
                                <IconButton onClick={() => removeDate(dateIndex)} color="error" size="small">
                                    <RemoveIcon />
                                </IconButton>
                            )}
                        </Grid>
                    </Grid>
                ))}
            </Box>
        </Grid>
    );
}

export default function HolidayListAdd({ onSuccess }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const validationSchema = Yup.object().shape({
        holidays: Yup.array().of(
            Yup.object().shape({
                hTYpe: Yup.string().required("Holiday type is required").oneOf(["NATIONAL", "REGIONAL"], "Must be NATIONAL or REGIONAL"),
                fromToPincodes: Yup.object().when("hTYpe", {
                    is: "REGIONAL",
                    then: (schema) => schema.shape({
                        to: Yup.string().required("To pincode is required for regional holidays"),
                    }),
                    otherwise: (schema) => schema.nullable(),
                }),
                dates: Yup.array().of(
                    Yup.object().shape({
                        date: Yup.date().required("Date is required"),
                        event: Yup.string().required("Event name is required"),
                        day: Yup.string().required("Day is required"),
                    })
                ).min(1, "At least one date is required"),
            })
        ).min(1, "At least one holiday entry is required"),
    });

    const methods = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            holidays: [{
                hTYpe: "",
                fromToPincodes: null,
                dates: [{ date: null, event: "", day: "" }],
            }],
        },
    });

    const { handleSubmit, reset, control } = methods;

    const { fields: holidayFields, append: appendHoliday, remove: removeHoliday } = useFieldArray({
        control,
        name: "holidays",
    });

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            const payload = data.holidays.map((holiday) => ({
                hTYpe: holiday.hTYpe,
                fromToPincodes: holiday.hTYpe === "REGIONAL" && holiday.fromToPincodes?.to
                    ? { to: holiday.fromToPincodes.to }
                    : null,
                dates: holiday.dates.map((dateItem) => ({
                    date: format(new Date(dateItem.date), "yyyy-MM-dd"),
                    event: dateItem.event,
                    day: dateItem.day,
                })),
            }));

            const response = await postGeneric(apiConstants.ADD_EDDHOLIDAYS, payload);

            if (response.data) {
                enqueueSnackbar("Holiday list submitted successfully!", { variant: 'success' });
                reset();
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error("Error submitting holiday list:", error);
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to submit holiday list";
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Alert severity="info" variant="outlined" sx={{ '& .MuiAlert-message': { fontSize: 13 } }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Manually add holidays for EDD (Estimated Delivery Date) calculation</Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                            Holidays added here apply to <strong>all merchants</strong> and will be excluded when calculating delivery dates.
                            Choose <strong>National</strong> for country-wide holidays or <strong>Regional</strong> for pincode-specific ones.
                            You can add multiple dates per entry and multiple entries at once.
                            For bulk uploads, use the <strong>Upload Holiday List</strong> tab instead.
                        </Typography>
                    </Alert>
                </Grid>

                {holidayFields.map((holidayField, holidayIndex) => (
                    <HolidayEntry
                        key={holidayField.id}
                        holidayIndex={holidayIndex}
                        onRemove={() => removeHoliday(holidayIndex)}
                        canRemove={holidayFields.length > 1}
                    />
                ))}

                <Grid item xs={12}>
                    <Button
                        onClick={() => appendHoliday({
                            hTYpe: "",
                            fromToPincodes: null,
                            dates: [{ date: null, event: "", day: "" }],
                        })}
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        size="small"
                        fullWidth
                        sx={{ borderStyle: 'dashed' }}
                    >
                        Add Another Holiday Entry
                    </Button>
                </Grid>

                <Grid item xs={12} sx={{ display: 'flex', gap: 1.5 }}>
                    <Button variant="contained" color="error" size="small" onClick={() => reset()}>
                        Reset
                    </Button>
                    <LoadingButton
                        loading={isSubmitting}
                        variant="contained"
                        color="primary"
                        type="submit"
                        size="small"
                    >
                        Submit Holiday List
                    </LoadingButton>
                </Grid>
            </Grid>
        </FormProvider>
    );
}

